/**
 * Proscan AI document extraction logs.
 * The list/retrieve endpoints are plain paginated reads, so they go through the generic
 * `fetchWithQuery`/`fetchById` actions using the payload in
 * `@/components/RouteComponents/Proscan/actionPayload`. Only the multipart upload and the
 * file download need their own actions, which live here.
 */
import Axios from 'axios'
import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_PROSCAN_LOGS,
  RECEIVING_PROSCAN_LOGS,
  RECEIVE_ALL_PROSCAN_LOGS,
  RECEIVE_PROSCAN_LOG_METADATA,
  RECEIVE_PROSCAN_LOG_FAILURE,
  UPDATE_CURRENT_PROSCAN_LOG_PAGE,
  UPDATE_PROSCAN_LOG_ROWS,
  PREPEND_UPLOADED_PROSCAN_LOGS,
  FILTER_PROSCAN_QUERY,
  CLEAR_FILTER_PROSCAN_QUERY,
  SAVING_PROSCAN_LOG,
  SAVING_PROSCAN_LOG_FAILURE,
  UPDATING_PROSCAN_LOG,
  UPDATING_PROSCAN_LOG_FAILURE,
  RESET_PROSCAN_LOG_META,
  CLEAR_PROSCAN_LOG_STATE,
  INCREMENT_PROSCAN_LOG_COUNT,
  SET_PROSCAN_UPLOAD_BATCH,
  UPDATE_PROSCAN_UPLOAD_BATCH_ROWS,
  SET_PROSCAN_UPLOAD_BATCH_VISIBILITY,
  CLEAR_PROSCAN_UPLOAD_BATCH,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const ENDPOINT = 'proscan-data-extractions'

const UPLOAD_BATCH_POLL_INTERVAL = 5000
let uploadBatchPollTimer = null

// log_id is the primary key on these rows, not id
// const proscanLogSchema = new schema.Entity('proscanLogs', undefined, {
//   idAttribute: value => value.log_id
// })
const proScanLogSchema = new schema.Entity('proscanLogs')
const proScanLogListSchema = new schema.Array(proScanLogSchema)

const state = {
  data: {},
  all: [],
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  filterInfo: {},
  updating: null,
  updateFailure: null,
  // The current upload batch, tracked independently of the paginated list so per-file
  // outcomes survive navigating away from page 1. Each entry:
  //   { log_id, file_name, status, info }
  uploadBatch: [],
  // Whether the persistent upload-batch panel is shown.
  uploadBatchVisible: false
}

/**
 * Builds the base url for the extraction endpoint, tolerating an instance url with or
 * without a trailing slash.
 * @param instanceUrl
 * @return {string}
 */
const baseUrl = instanceUrl => {
  return `${instanceUrl.endsWith('/') ? instanceUrl.slice(0, -1) : instanceUrl}/${ENDPOINT}`
}

const actions = {
  /**
   * Uploads one or more documents for AI extraction. The API creates one log row (and one
   * background task) per file and returns them as a plain array, already IN_PROGRESS.
   * @param store
   * @param payload  { documentType, batch, files }
   * @return {Promise<Array>}  The created log rows
   */
  uploadProscanDocuments (store, payload) {
    const { documentType, batch, files } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const token = this.getters.getToken
    const form = new FormData()
    form.append('document_type', documentType)
    if (batch) form.append('batch', batch)
    for (let i = 0; i < files.length; i++) {
      form.append('files', files[i])
    }
    // form.append('files', [...files])
    this.commit(SAVING_PROSCAN_LOG, true)
    this.commit(SAVING_PROSCAN_LOG_FAILURE, null)
    return Axios.post(baseUrl(instanceUrl), form, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => {
        this.commit(SAVING_PROSCAN_LOG, false)
        const results = Array.isArray(r.data) ? r.data : [r.data]
        this.commit(INCREMENT_PROSCAN_LOG_COUNT, results.length)
        return results
      })
      .catch(e => {
        this.commit(SAVING_PROSCAN_LOG, false)
        this.commit(SAVING_PROSCAN_LOG_FAILURE, handleXHRError(e))
        return Promise.reject(e)
      })
  },
  /**
   * Fetches the given extraction logs by id, one request each (the list endpoint only
   * filters log_id exactly). Used to poll IN_PROGRESS uploads without disturbing the
   * paginated list state. Rows that fail to load are skipped so polling can continue.
   * @param store
   * @param logIds  Array of log_id strings
   * @return {Promise<Array>}
   */
  fetchProscanLogsByIds (store, logIds) {
    if (!(logIds && logIds.length)) return Promise.resolve([])
    const instanceUrl = this.getters.getActiveInstance.url
    const token = this.getters.getToken
    const headers = { 'Authorization': `Bearer ${token}` }
    return Promise.all(logIds.map(logId => {
      return Axios.get(`${baseUrl(instanceUrl)}/${logId}`, { headers })
        .then(r => r.data)
        .catch(e => {
          console.log(e)
          return null
        })
    }))
      .then(rows => rows.filter(row => row && row.log_id))
  },
  /**
   * Fetches a single extraction log, including the heavy fields (data, raw_output, tokens)
   * that the list endpoint omits.
   * @param store
   * @param logId
   * @return {Promise<Object|null>}
   */
  fetchProscanLogById (store, logId) {
    const instanceUrl = this.getters.getActiveInstance.url
    const token = this.getters.getToken
    this.commit(RECEIVING_PROSCAN_LOGS, true)
    return Axios.get(`${baseUrl(instanceUrl)}/${logId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => {
        this.commit(RECEIVING_PROSCAN_LOGS, false)
        return r.data
      })
      .catch(e => {
        this.commit(RECEIVING_PROSCAN_LOGS, false)
        this.commit(RECEIVE_PROSCAN_LOG_FAILURE, handleXHRError(e))
        return null
      })
  },
  /**
   * Resolves the signed download url for an uploaded document, the same way the transaction
   * document dialog does, so the file can be rendered inline without sending the auth token
   * along with the file request.
   * @param store
   * @param fileId
   * @return {Promise<string|null>}
   */
  fetchFileDownloadUrl (store, fileId) {
    if (!fileId) return Promise.resolve(null)
    const instanceUrl = this.getters.getActiveInstance.url
    const token = this.getters.getToken
    const url = `${instanceUrl.endsWith('/') ? instanceUrl.slice(0, -1) : instanceUrl}/files/${fileId}/download`
    return Axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.data && r.data.download_url) || null)
      .catch(e => {
        console.log(e)
        return null
      })
  },
  /**
   * Lists the documents attached to a transaction, e.g. `/aps/12/files`, for the document
   * pane's file picker.
   * @param store
   * @param path  Transaction path, without a leading or trailing slash, e.g. 'aps/12'
   * @return {Promise<Array>}
   */
  fetchTransactionFiles (store, path) {
    const instanceUrl = this.getters.getActiveInstance.url
    const token = this.getters.getToken
    const base = instanceUrl.endsWith('/') ? instanceUrl.slice(0, -1) : instanceUrl
    return Axios.get(`${base}/${path}/files`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.data && r.data.results) || [])
      .catch(e => {
        console.log(e)
        return []
      })
  },
  /* Fetches one or more Proscan Logs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchProscanLogsWithQuery (store, opts) {
    let query
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
    } else {
      query = {}
    }
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PROSCAN_LOGS, true)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('proscan-data-extractions', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RECEIVING_PROSCAN_LOGS, false)
      this.commit(RESET_PROSCAN_LOG_META)
      let { results, next, previous, count } = resp
      // The API returns no ids, but normalizr keys proscanLogs entities by `id`.
      // Assign a sequence number per page so each record normalizes to its own entity.
      results = results.map((v, i) => {
        v.id = ((query.page || 1) - 1) * (resp.page_size || 20) + (i + 1)
        return v
      })

      const { result, entities } = normalize(results, proScanLogListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.proscanLogs }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }

      this.commit(RECEIVE_PROSCAN_LOGS, mutationPayload)
      this.commit(RECEIVE_PROSCAN_LOG_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_PROSCAN_LOG_PAGE, query && query.page || 1)
      return { query, count, entities, previous, next }
    } catch (e) {
      this.commit(RECEIVING_PROSCAN_LOGS, false)
      this.commit(RECEIVE_PROSCAN_LOG_FAILURE, handleXHRError(e))
      console.log(e)
      if (e.status === 403) {
        window.location.href = '/403'
      }
    }
  },
  /**
   * Merges freshly polled rows into the list in place, matched by log_id, so an IN_PROGRESS
   * row's status (and the fields that resolve with it) update on screen without refetching or
   * reloading the page. Rows not currently held are ignored.
   * @param store
   * @param rows  The updated log rows from a poll
   */
  updateProscanLogRows (store, rows) {
    if (!(rows && rows.length)) return
    this.commit(UPDATE_PROSCAN_LOG_ROWS, rows)
  },
  /**
   * @param store
   * @param rows  The log rows the upload created, all IN_PROGRESS
   */
  prependUploadedProscanRows (store, rows) {
    if (!(rows && rows.length)) return
    this.commit(PREPEND_UPLOADED_PROSCAN_LOGS, rows)
  },
  async fetchInProgressProscanLogs (store) {
    const instanceUrl = this.getters.getActiveInstance && this.getters.getActiveInstance.url
    const token = this.getters.getToken
    if (!(instanceUrl && token)) return []
    const headers = getHeaders(token)
    const query = { status__in: 'IN_PROGRESS', page_size: 200 }  // Upper bound on how many still-extracting files a refresh will rebuild the panel from
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('proscan-data-extractions', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      return (resp && resp.results) || []
    } catch (e) {
      console.log(e)
      return []
    }
  },
  async resumeProscanUploadBatch (store) {
    if (store.state.uploadBatch.length) {
      store.dispatch('startProscanUploadBatchPolling')
      return
    }
    const rows = await store.dispatch('fetchInProgressProscanLogs')
    if (rows && rows.length) store.dispatch('seedProscanUploadBatch', rows)
  },
  /**
   * @param store
   * @param rows  The log rows the upload created, all IN_PROGRESS
   */
  seedProscanUploadBatch (store, rows) {
    if (!(rows && rows.length)) return
    const batch = rows
      .filter(row => row && row.log_id)
      .map(row => ({
        log_id: row.log_id,
        file_name: row.file_name || '',
        status: row.status || 'IN_PROGRESS',
        info: row.info || ''
      }))
    if (!batch.length) return
    this.commit(SET_PROSCAN_UPLOAD_BATCH, batch)
    this.commit(SET_PROSCAN_UPLOAD_BATCH_VISIBILITY, true)
    store.dispatch('startProscanUploadBatchPolling')
  },
  /**
   * @param store
   */
  startProscanUploadBatchPolling (store) {
    store.dispatch('stopProscanUploadBatchPolling')
    const pendingIds = () => store.state.uploadBatch
      .filter(f => f.status === 'IN_PROGRESS')
      .map(f => f.log_id)
    if (!pendingIds().length) return
    uploadBatchPollTimer = setInterval(async () => {
      const ids = pendingIds()
      if (!ids.length) return store.dispatch('stopProscanUploadBatchPolling')
      const rows = await store.dispatch('fetchProscanLogsByIds', ids)
      if (rows.length) {
        this.commit(UPDATE_PROSCAN_UPLOAD_BATCH_ROWS, rows)
        // Keep the visible list in sync without reloading the page.
        this.commit(UPDATE_PROSCAN_LOG_ROWS, rows)
      }
      if (!pendingIds().length) store.dispatch('stopProscanUploadBatchPolling')
    }, UPLOAD_BATCH_POLL_INTERVAL)
  },
  stopProscanUploadBatchPolling () {
    if (uploadBatchPollTimer) {
      clearInterval(uploadBatchPollTimer)
      uploadBatchPollTimer = null
    }
  },
  setProscanUploadBatchVisibility (store, visible) {
    this.commit(SET_PROSCAN_UPLOAD_BATCH_VISIBILITY, !!visible)
  },
  clearProscanUploadBatch (store) {
    store.dispatch('stopProscanUploadBatchPolling')
    this.commit(CLEAR_PROSCAN_UPLOAD_BATCH)
  },
  clearProscanLogState (store, payload = null) {
    return this.commit(CLEAR_PROSCAN_LOG_STATE, payload)
  },
  resetProscanLogMeta () {
    this.commit(RESET_PROSCAN_LOG_META)
  },
  setProscanFilterInfo (store, query) {
    this.commit(FILTER_PROSCAN_QUERY, query)
  },
  clearSetProscanFilterInfo (store) {
    this.commit(CLEAR_FILTER_PROSCAN_QUERY)
  }
}

const getters = {
  getCurrentProscanLogPage: state => state.currentPage,
  getProscanLogFetchStatus: state => state.receivingData,
  getProscanLogSaveStatus: state => state.saving,
  getProscanLogSaveFailure: state => state.saveFailure,
  getProscanFilterInfo: (state) => {
    return state.filterInfo
  },
  getProscanLogs: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getProscanLogsCache: (state) => {
    return state.cache ? Object.values(state.cache).map(log => Object.assign({}, log)) : []
  },
  getProscanLogById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllProscanLogs: (state) => {
    return state.all || []
  },
  getProscanLogMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getCurrentProscanPage: (state) => {
    if (state.currentPage) return state.currentPage
  },
  getProscanUploadBatch: (state) => state.uploadBatch,
  getProscanUploadBatchVisible: (state) => state.uploadBatchVisible,
  /**
   * Rolls the batch up into counts for the panel header.
   */
  getProscanUploadBatchSummary: (state) => {
    const summary = { total: state.uploadBatch.length, inProgress: 0, successful: 0, failed: 0 }
    state.uploadBatch.forEach(f => {
      if (f.status === 'SUCCESSFUL') summary.successful++
      else if (f.status === 'FAILED') summary.failed++
      else summary.inProgress++
    })
    return summary
  }
}

const mutations = {
  [RECEIVE_PROSCAN_LOGS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_PROSCAN_LOGS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_PROSCAN_LOG_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [RECEIVE_PROSCAN_LOG_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [SAVING_PROSCAN_LOG] (state, payload) {
    state.saving = payload
  },
  [SAVING_PROSCAN_LOG_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_PROSCAN_LOG] (state, payload) {
    state.updating = payload
  },
  [UPDATING_PROSCAN_LOG_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [INCREMENT_PROSCAN_LOG_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RESET_PROSCAN_LOG_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_FILTER_PROSCAN_QUERY] (state) {
    state.filterInfo = {}
  },
  [RECEIVING_PROSCAN_LOGS] (state, payload) {
    state.receivingData = payload
  },
  [FILTER_PROSCAN_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword') && query.keyword !== '') {
      state.filterInfo.keyword = query.keyword
    }
  },
  [RECEIVE_ALL_PROSCAN_LOGS] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_PROSCAN_LOG_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [INCREMENT_PROSCAN_LOG_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_PROSCAN_LOG_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [UPDATE_CURRENT_PROSCAN_LOG_PAGE] (state, payload) {
    state.currentPage = payload
  },
  /**
   * Updates held rows in place from a poll, matched by log_id. The list is keyed by a
   * per-page `id`, not by log_id, so the incoming fields are merged onto the matching entry
   * and its `id`/`page` are kept. New objects are assigned so the grid re-renders the change.
   */
  [UPDATE_PROSCAN_LOG_ROWS] (state, rows) {
    const byLogId = {}
    rows.forEach(row => { if (row && row.log_id) byLogId[row.log_id] = row })
    const merge = (bucket) => {
      let changed = false
      const next = Object.assign({}, bucket)
      for (const key in next) {
        const incoming = next[key] && byLogId[next[key].log_id]
        if (!incoming) continue
        next[key] = Object.assign({}, next[key], incoming, { id: next[key].id, page: next[key].page })
        changed = true
      }
      return changed ? next : bucket
    }
    if (state.data) state.data = merge(state.data)
    if (state.cache) state.cache = merge(state.cache)
  },
  [PREPEND_UPLOADED_PROSCAN_LOGS] (state, rows) {
    const existing = state.data ? Object.values(state.data) : []
    const existingLogIds = new Set(existing.map(r => r && r.log_id))
    const missing = rows.filter(r => r && r.log_id && !existingLogIds.has(r.log_id))
    if (!missing.length) return
    const combined = [...missing, ...existing]
    const data = {}
    combined.forEach((row, i) => {
      const id = i + 1
      data[id] = Object.assign({}, row, { id, page: 1 })
    })
    // state.data = data
    state.shown = combined.length
  },
  [SET_PROSCAN_UPLOAD_BATCH] (state, batch) {
    state.uploadBatch = batch
  },
  [UPDATE_PROSCAN_UPLOAD_BATCH_ROWS] (state, rows) {
    const byLogId = {}
    rows.forEach(row => { if (row && row.log_id) byLogId[row.log_id] = row })
    state.uploadBatch = state.uploadBatch.map(f => {
      const incoming = byLogId[f.log_id]
      if (!incoming) return f
      return Object.assign({}, f, {
        status: incoming.status || f.status,
        info: incoming.info != null ? incoming.info : f.info,
        file_name: f.file_name || incoming.file_name || ''
      })
    })
  },
  [SET_PROSCAN_UPLOAD_BATCH_VISIBILITY] (state, visible) {
    state.uploadBatchVisible = visible
  },
  [CLEAR_PROSCAN_UPLOAD_BATCH] (state) {
    state.uploadBatch = []
    state.uploadBatchVisible = false
  },
  [CLEAR_PROSCAN_LOG_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_PROSCAN_LOG_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
    if (uploadBatchPollTimer) {
      clearInterval(uploadBatchPollTimer)
      uploadBatchPollTimer = null
    }
    state.uploadBatch = []
    state.uploadBatchVisible = false
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/proscan.js